import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import { Save, UserPlus, UserCheck, Calendar } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { capitalizeText } from "@/lib/utils";
import { useStudents } from "@/hooks/useSupabaseData";
import { useStudentActivities } from "@/hooks/useStudentActivities";
import { StudentBasicForm } from "@/components/student/StudentBasicForm";
import { DefaultBillingConfig } from "@/components/student/DefaultBillingConfig";
import { StudentActivitiesConfig } from "@/components/student/StudentActivitiesConfig";

interface StudentFormProps {
  student?: any;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess: () => void;
}

export const StudentForm = ({ student, open, onOpenChange, onSuccess }: StudentFormProps) => {
  const [loading, setLoading] = useState(false);
  const { refetch: refetchStudents } = useStudents();
  const [formData, setFormData] = useState({
    name: '',
    class: '',
    email: '',
    phone: ''
  });
  const [defaultBillingType, setDefaultBillingType] = useState<'per_session' | 'monthly'>('monthly');
  const { toast } = useToast();
  const { activities, loading: activitiesLoading, updateScheduledDays, updateBillingTypeOverride } = useStudentActivities(student?.id);

  // Database stores ISO standard: 1=Monday, 2=Tuesday, ..., 5=Friday, 6=Saturday, 0=Sunday
  const dayNames = ['Domingo', 'Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado']; // Index 0=Sunday, 1=Monday, etc.
  const dayAbbreviations = ['D', 'L', 'M', 'X', 'J', 'V', 'S']; // Index 0=Sunday, 1=Monday, etc.

  // Reset form when modal opens/closes or student changes
  useEffect(() => {
    if (student) {
      setFormData({
        name: student.name || '',
        class: student.class || '',
        email: student.email || '',
        phone: student.phone || ''
      });
      // Para estudiantes existentes, mantener su configuración actual o usar 'monthly' como defecto
      setDefaultBillingType('monthly');
    } else {
      setFormData({
        name: '',
        class: '',
        email: '',
        phone: ''
      });
      setDefaultBillingType('monthly'); // Facturación mensual por defecto para nuevos estudiantes
    }
  }, [student, open]);

  const handleFormDataChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const validateForm = () => {
    if (!formData.name.trim()) {
      toast({
        title: "Error",
        description: "El nombre del estudiante es obligatorio.",
        variant: "destructive"
      });
      return false;
    }

    if (!formData.class.trim()) {
      toast({
        title: "Error", 
        description: "La clase del estudiante es obligatoria.",
        variant: "destructive"
      });
      return false;
    }

    if (formData.email && !isValidEmail(formData.email)) {
      toast({
        title: "Error",
        description: "Por favor, introduce un email válido.",
        variant: "destructive"
      });
      return false;
    }

    return true;
  };

  const isValidEmail = (email: string) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) return;

    try {
      setLoading(true);

      const capitalizedName = capitalizeText(formData.name.trim());
      
      const studentData = {
        name: capitalizedName,
        class: formData.class.trim(),
        email: formData.email.trim() || null,
        phone: formData.phone.trim() || null,
        default_billing_type: student ? undefined : defaultBillingType, // Solo al crear
        updated_at: new Date().toISOString()
      };

      if (student) {
        // Actualizar estudiante existente
        const { error } = await supabase
          .from('students')
          .update(studentData)
          .eq('id', student.id);

        if (error) throw error;

        toast({
          title: "Estudiante actualizado",
          description: `${formData.name} ha sido actualizado exitosamente.`
        });
      } else {
        // Crear nuevo estudiante
        const { data: newStudent, error } = await supabase
          .from('students')
          .insert(studentData)
          .select()
          .single();

        if (error) throw error;

        // Para nuevos estudiantes, la configuración de facturación por defecto
        // se aplicará cuando se inscriban en actividades específicas
        toast({
          title: "Estudiante creado",
          description: `${formData.name} ha sido añadido exitosamente con facturación ${defaultBillingType === 'monthly' ? 'mensual' : 'por día'} como predeterminada.`
        });

        console.log(`📝 Nuevo estudiante creado con billing type por defecto: ${defaultBillingType}`, newStudent);
      }

      onSuccess();
      onOpenChange(false);
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message || "No se pudo guardar el estudiante.",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDayChange = async (activityId: string, day: number, checked: boolean) => {
    const activity = activities.find(a => a.activityId === activityId);
    if (!activity) return;

    const newDays = checked 
      ? [...activity.scheduledDays, day].sort()
      : activity.scheduledDays.filter(d => d !== day);

    await updateScheduledDays(activityId, newDays);
  };

  const handleBillingTypeChange = async (activityId: string, billingType: string) => {
    const billingTypeValue = billingType === 'default' ? null : billingType as 'per_session' | 'monthly';
    await updateBillingTypeOverride(activityId, billingTypeValue);
  };

  const getEffectiveBillingType = (activity: any) => {
    // If there's an override, use it
    if (activity.billingTypeOverride) {
      return activity.billingTypeOverride;
    }
    
    // Otherwise, use the first available billing type as default
    return activity.availableBillingTypes[0] || 'per_session';
  };

  const getBillingTypeDisplayText = (billingType: 'per_session' | 'monthly') => {
    return billingType === 'per_session' ? 'Por día' : 'Mensual';
  };

  const renderActivitySchedule = (activity: any) => {
    if (!activity.requiresDaySelection) {
      return (
        <Badge variant="secondary" className="text-xs">
          Todos los días
        </Badge>
      );
    }

    return (
      <div className="space-y-2">
        <div className="flex flex-wrap gap-1">
          {dayAbbreviations.map((dayAbb, index) => {
            const isScheduled = activity.scheduledDays.includes(index);
            return (
              <Badge 
                key={index}
                variant={isScheduled ? "default" : "outline"}
                className="text-xs cursor-pointer"
              >
                {dayAbb}
              </Badge>
            );
          })}
        </div>
        
        <div className="grid grid-cols-2 gap-2 mt-2">
          {dayNames.map((dayName, index) => (
            <div key={index} className="flex items-center space-x-2">
              <Checkbox
                id={`${activity.activityId}-day-${index}`}
                checked={activity.scheduledDays.includes(index)}
                onCheckedChange={(checked) => handleDayChange(activity.activityId, index, checked as boolean)}
              />
              <Label 
                htmlFor={`${activity.activityId}-day-${index}`}
                className="text-sm font-normal cursor-pointer"
              >
                {dayName}
              </Label>
            </div>
          ))}
        </div>
      </div>
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {student ? (
              <>
                <UserCheck className="w-5 h-5" />
                Editar Estudiante
              </>
            ) : (
              <>
                <UserPlus className="w-5 h-5" />
                Nuevo Estudiante
              </>
            )}
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Datos básicos */}
          <StudentBasicForm 
            formData={formData}
            onFormDataChange={handleFormDataChange}
          />

          {/* Configuración de facturación por defecto */}
          <DefaultBillingConfig
            selectedType={defaultBillingType}
            onTypeChange={setDefaultBillingType}
            isCreating={!student}
          />

          {/* Actividades y Horarios (solo para estudiantes existentes) */}
          {student && (
            <>
              <Separator />
              <div className="space-y-4">
                <div className="flex items-center gap-2">
                  <Calendar className="w-5 h-5" />
                  <h3 className="text-lg font-semibold">Actividades y Horarios</h3>
                </div>
                
                <StudentActivitiesConfig
                  activities={activities}
                  activitiesLoading={activitiesLoading}
                  onDayChange={handleDayChange}
                  onBillingTypeChange={handleBillingTypeChange}
                  getEffectiveBillingType={getEffectiveBillingType}
                  getBillingTypeDisplayText={getBillingTypeDisplayText}
                  renderActivitySchedule={renderActivitySchedule}
                />
              </div>
            </>
          )}

          <div className="flex justify-end space-x-2 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={loading}
            >
              Cancelar
            </Button>
            <Button type="submit" disabled={loading}>
              <Save className="w-4 h-4 mr-2" />
              {student ? 'Actualizar' : 'Crear'} Estudiante
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};
